import React, { useState } from 'react';
import { Package } from 'lucide-react';
import { setInventoryItemStockTracked } from '../../utils/inventoryApi';

interface ConsumableToggleProps {
  practiceId: string;
  catalogType: 'product' | 'medication' | 'immunization' | 'diagnostic' | 'procedure' | 'treatment';
  catalogItemId: string;
  isStockTracked: boolean;
  onToggled?: (newValue: boolean, effectiveCatalogItemId: string) => void;
  disabled?: boolean;
}

/**
 * A toggle button for controlling whether a catalog item is inventory/stock tracked.
 * 
 * When toggled off then on, history is preserved - no data loss.
 * This calls the backend API to update is_stock_tracked on the catalog item.
 */
export const ConsumableToggle: React.FC<ConsumableToggleProps> = ({
  practiceId,
  catalogType,
  catalogItemId,
  isStockTracked,
  onToggled,
  disabled = false,
}) => {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async () => {
    if (updating || disabled) return;
    
    console.log('[ConsumableToggle] handleToggle called');
    console.log('[ConsumableToggle] Current isStockTracked prop:', isStockTracked, typeof isStockTracked);
    console.log('[ConsumableToggle] Will send is_stock_tracked:', !isStockTracked);
    
    setUpdating(true);
    setError(null);
    
    try {
      const result = await setInventoryItemStockTracked(
        practiceId,
        catalogType,
        catalogItemId,
        !isStockTracked
      );
      console.log('[ConsumableToggle] API result:', result);
      
      onToggled?.(result.is_stock_tracked, result.effective_catalog_item_id);
    } catch (err: any) {
      setError(err?.message || 'Failed to update');
      console.error('Failed to toggle stock tracking:', err);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onClick={handleToggle}
        disabled={updating || disabled}
        className={`
          inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
          border transition-colors
          ${updating ? 'opacity-50 cursor-wait' : disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isStockTracked
            ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800 hover:bg-amber-200 dark:hover:bg-amber-900/40'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
          }
        `}
        title={isStockTracked ? 'Inventory tracked - click to disable' : 'Not inventory tracked - click to enable'}
      >
        <Package className="w-3.5 h-3.5" />
        {updating ? (
          <span className="animate-pulse">...</span>
        ) : (
          <span>{isStockTracked ? 'Consumable' : 'Not Consumable'}</span>
        )}
      </button>
      
      {error && (
        <div className="absolute top-full left-0 mt-1 px-2 py-1 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 text-xs rounded shadow-lg whitespace-nowrap z-10">
          {error}
        </div>
      )}
    </div>
  );
};

export default ConsumableToggle;

